/**
 * AN INVOICE'S DOCUMENTS, the part at the foot of its own page (ops's `docs/seams/home.md` section
 * 11.2, live on both of ops's hosts from 2026-10-17; round five's slice B3, `docs/seam.md` P62). No
 * artboard draws it (business issue 13), so it is built from the page's own parts, Payments' outlined
 * button inside a card and a heading as "How did we do?" is headed, in the words ops's own page draws
 * (`DOCUMENTS`, `web/src/words.ts`). A client's alone, as the page is: a member is shown no invoice.
 *
 * - WHAT IS OFFERED: each of the invoice's PDFs ops says is made, in the order ops made them, as a
 *   button in ops's words: the invoice's own, "Download the invoice (PDF)"; each receipt, "Receipt
 *   FK-R7 (PDF)", by its own number; and a cancellation, which ops offers at home's door though never
 *   through her link, "Invoice FK-41, cancelled (PDF)", by the invoice's number. While ops says one
 *   is being made, its own place in that order names it, "Receipt FK-R7" (`documentName`), with "Your
 *   PDF is being made. It will be here in a few minutes." under it, ops's words, and offers nothing
 *   for it (section 11.2's `ready`): never one unnamed line under another PDF's button, which the
 *   replay of 2026-10-18 read as about the invoice when it was about the receipt (round six, slice
 *   S1). A kind ops adds later offers nothing, and nor does a receipt or a cancellation without the
 *   number its words name it by. With nothing to offer or to say, no part is drawn.
 * - A PRESS (`DocumentButton`) sends one `invoice.document` with the invoice and the document through
 *   the request door, under a key minted for the press and kept only while the door could not be
 *   reached; the button says "Getting your PDF", ops's words, with the name of the PDF it gets under
 *   them (slice S1), busy, while the page asks the Worker where it stands every three seconds, as Pay
 *   asks after Stripe's page (`POLL_GAP_MS`), but past the drain's longest and once more, ten times at
 *   most (`DOCUMENT_POLLS`): the drain sends the press and then reads the PDF, up to twenty-five
 *   seconds in all, where a pay's six asks wait on a send alone (the review of 7e33d61, findings 2
 *   and 3).
 *   Once the Worker says it holds the PDF, which the drain of her press read from ops, the page
 *   points this same tab at home's own address for it (`documentFile`, `away.to`; ops #76, ask 4),
 *   never at an address of ops's, as Pay sends her to Stripe's page, and never a window opened
 *   after a wait, which a browser blocks. The address is never drawn and kept nowhere.
 * - WHAT COMES OF IT: past the poll, or on any refusal but one, the could-not-get line in its live
 *   region, the office's number a link, and the button to press again, which is a new request (a
 *   new press is a new request, section 11.2); ops's `document_not_ready`, from the door or the poll,
 *   says the PDF's name and the being-made line in place of the button, the line with the focus, and
 *   reads the invoice again; the door's own limits say their own line, with whose count and when;
 *   and a session that has ended goes to the door.
 * - A LINK THAT NAMES ONE OF THE PDFS (`?document=`, the owner's decision of 2026-10-19, ops #76,
 *   `docs/seam.md` P107): the invoice's page says which, where the invoice offers it and ops says it
 *   is made (`documentOnArrival`), and that PDF's button presses itself once as it is first drawn,
 *   the same press, the same request and the same poll as her own, saying so in its words. A PDF
 *   still being made, one the invoice does not offer, or none, presses nothing: the part is brought
 *   into view with its usual words (`screens/Invoice.tsx`).
 */
import { useEffect, useId, useRef, useState, type Ref, type RefObject } from 'react';
import {
  askForDocument,
  documentFile,
  getDocument,
  type AskAnswer,
  type DocumentAnswer,
  type InvoiceDocument,
  type TooMany,
} from '../api.ts';
import { away } from '../lib/paying.ts';
import {
  cancellationPdf,
  documentName,
  DOCUMENTS,
  receiptPdf,
  tooManyBeforePhone,
} from '../words.ts';
import { CARD_BUTTON } from './buttons.ts';
import type { Signed } from './Guarded.tsx';
import { POLL_GAP_MS } from './Leaving.tsx';
import { Phone } from './Phone.tsx';

/**
 * THE LONGEST THE DRAIN OF HER PRESS MAY TAKE before the Worker holds the PDF (the review of 7e33d61,
 * findings 2 and 3): one send's fifteen seconds, then the read's own ten, body included
 * (`UP_TIMEOUT_MS` and `DOCUMENT_READ_TIMEOUT_MS`, `api/src/seam/outbox.ts`), her press reading
 * `sending` until both are done. `api/test/documents.test.ts` holds this to the two.
 */
export const DRAIN_LONGEST_MS = 25_000;

/**
 * How many times at most a press asks where it stands: past the drain's longest, and once more, so
 * her page never says it could not get a PDF while the drain may still be reading it. A pay's page
 * waits on a send alone, and asks `POLLS` times, as before.
 */
export const DOCUMENT_POLLS = Math.ceil(DRAIN_LONGEST_MS / POLL_GAP_MS) + 1;

/** A document home offers, with the words its button says and its name. */
interface Offered {
  readonly id: string;
  readonly label: string;
  /** Its name, said where it is being made and under "Getting your PDF" (`documentName`). */
  readonly name: string;
  readonly ready: boolean;
}

/**
 * The words a document's button says, in ops's words, or null for one home does not offer: a kind
 * this build has no words for, or a receipt or a cancellation without the number that names it.
 */
function labelOf(document: InvoiceDocument): string | null {
  switch (document.kind) {
    case 'invoice':
      return DOCUMENTS.invoice;
    case 'receipt':
      return document.number === undefined ? null : receiptPdf(document.number);
    case 'cancellation':
      return document.number === undefined ? null : cancellationPdf(document.number);
    default:
      return null;
  }
}

/**
 * Each document home offers, in ops's order, with its words, its name and whether ops says it is
 * made.
 */
export function offeredOf(documents: readonly InvoiceDocument[] | undefined): Offered[] {
  return (documents ?? []).flatMap((document) => {
    const label = labelOf(document);
    const name = documentName(document.kind, document.number);
    return label === null || name === null
      ? []
      : [{ id: document.id, label, name, ready: document.ready }];
  });
}

/**
 * What a link that names one of the invoice's PDFs (`?document=`, P107) comes to as the page is
 * drawn: `press`, that PDF, where the invoice offers it and ops says it is made; the part alone, to
 * bring into view with its usual words, where it names one still being made, one the invoice does
 * not offer, or none; and null where no PDF was named, or the invoice has no Documents part, which
 * is the invoice as usual.
 */
export function documentOnArrival(
  named: string | null,
  documents: readonly InvoiceDocument[] | undefined,
): { readonly press?: string } | null {
  if (named === null) return null;
  const offered = offeredOf(documents);
  if (offered.length === 0) return null;
  return offered.some((document) => document.id === named && document.ready)
    ? { press: named }
    : {};
}

/**
 * A PDF ops says is being made, in its own place: its name, then ops's line under it. `lineRef` is
 * given where the line takes the focus, as after a press ops answered so.
 */
function BeingMade({
  name,
  lineRef,
}: {
  readonly name: string;
  readonly lineRef?: Ref<HTMLParagraphElement>;
}) {
  return (
    <div>
      <p className="text-body font-semibold text-pretty text-text-body">{name}</p>
      <p
        ref={lineRef}
        tabIndex={lineRef === undefined ? undefined : -1}
        className="text-body text-pretty"
      >
        {DOCUMENTS.beingMade}
      </p>
    </div>
  );
}

interface DocumentsProps {
  readonly invoiceId: string;
  /** The household shown, which the request names. */
  readonly household: string;
  readonly documents: readonly InvoiceDocument[] | undefined;
  readonly signed: Signed;
  /** Reads the invoice again, for what ops says of its PDFs now. */
  readonly reload: () => void;
  /**
   * The PDF a link named, where it is offered and made, whose button presses itself once as it is
   * first drawn (P107); `onOpened` is told as it does, so it never does again.
   */
  readonly opening?: string;
  readonly onOpened?: () => void;
  /** The part, for the page to bring into view as it lands on it (P107). */
  readonly sectionRef?: RefObject<HTMLElement | null>;
}

/**
 * The Documents part: each PDF ops offers in its own place, in ops's order, as a button while ops
 * says it is made, and by its name with ops's being-made line while it is being made.
 */
export function Documents({
  invoiceId,
  household,
  documents,
  signed,
  reload,
  opening,
  onOpened,
  sectionRef,
}: DocumentsProps) {
  const headingId = useId();
  const offered = offeredOf(documents);
  if (offered.length === 0) return null;

  return (
    // A little room above it where the page brings it to the top of the screen.
    <section ref={sectionRef} aria-labelledby={headingId} className="scroll-mt-4">
      <h2 id={headingId} className="font-serif text-h3 font-semibold text-text-body">
        {DOCUMENTS.heading}
      </h2>
      <ul className="mt-3 flex flex-col gap-3">
        {offered.map((document) => (
          <li key={document.id}>
            {document.ready ? (
              <DocumentButton
                invoiceId={invoiceId}
                household={household}
                documentId={document.id}
                label={document.label}
                name={document.name}
                signed={signed}
                reload={reload}
                pressNow={document.id === opening}
                onPressed={onOpened}
              />
            ) : (
              <BeingMade name={document.name} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** What the line under a button says once a press has ended without the PDF. */
type Ended = 'not_got' | 'too_many';

interface ButtonProps {
  readonly invoiceId: string;
  readonly household: string;
  readonly documentId: string;
  readonly label: string;
  /** The PDF's name, said under "Getting your PDF" and where it is being made. */
  readonly name: string;
  readonly signed: Signed;
  readonly reload: () => void;
  /** A link named this PDF (P107): the button presses itself once, as it is first drawn. */
  readonly pressNow?: boolean;
  /** Told as it presses itself, so it never does again. */
  readonly onPressed?: () => void;
}

/** Ops's refusal while the PDF is still being made (section 11.2), from the door or the poll. */
const NOT_READY = 'document_not_ready';

/**
 * One PDF's button: a press, the wait while the page asks where it stands, and what came of it.
 */
export function DocumentButton({
  invoiceId,
  household,
  documentId,
  label,
  name,
  signed,
  reload,
  pressNow = false,
  onPressed,
}: ButtonProps) {
  /** A press is out: the button says so, and another press does nothing. */
  const [getting, setGetting] = useState(false);
  const out = useRef(false);
  /** How the last press ended, said under the button until she presses again. */
  const [ended, setEnded] = useState<Ended | null>(null);
  /** Past the door's own limits: whose count and when, as the door said them (slice S1). */
  const [limit, setLimit] = useState<TooMany>();
  /** Ops said the PDF is still being made: the being-made line stands in place of the button. */
  const [beingMade, setBeingMade] = useState(false);
  const madeLine = useRef<HTMLParagraphElement>(null);
  /** The key minted for the press, kept only while the door could not be reached. */
  const minted = useRef<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A page the browser brings back from its cache after the tab went to the PDF offers the button
  // again: the press that left is over.
  useEffect(() => {
    function cached(event: PageTransitionEvent) {
      if (!event.persisted) return;
      out.current = false;
      setGetting(false);
    }
    window.addEventListener('pageshow', cached);
    return () => window.removeEventListener('pageshow', cached);
  }, []);

  useEffect(() => {
    if (beingMade) madeLine.current?.focus();
  }, [beingMade]);

  /** The press has ended, as `how` says under the button, or with nothing to say. */
  function finish(how: Ended | null) {
    out.current = false;
    if (!mounted.current) return;
    setGetting(false);
    setEnded(how);
  }

  /** Ops says the PDF is still being made: said in place of the button, and the invoice read again. */
  function stillMaking() {
    finish(null);
    if (!mounted.current) return;
    setBeingMade(true);
    reload();
  }

  /**
   * Asks where the press stands, every three seconds, past the drain's longest and once more at most
   * (`DOCUMENT_POLLS`), and acts on it.
   */
  async function waitForLink(askId: string) {
    for (let asked = 0; asked < DOCUMENT_POLLS; asked += 1) {
      await new Promise((resolve) => setTimeout(resolve, POLL_GAP_MS));
      if (!mounted.current) return;
      const answer: DocumentAnswer = await getDocument(invoiceId, askId, household);
      if (!mounted.current) return;
      switch (answer.outcome) {
        case 'waiting':
        case 'unread':
        case 'offline':
          continue;
        case 'ready':
          // Home's own address, never one an answer brings (ops #76, ask 4).
          away.to(documentFile(invoiceId, documentId, household));
          finish(null);
          return;
        case 'refused':
          if (answer.code === NOT_READY) stillMaking();
          else finish('not_got');
          return;
        case 'signed_out':
          finish(null);
          signed.onSignedOut();
          return;
        case 'not_found':
          finish('not_got');
          reload();
          return;
        case 'failed':
        case 'stalled':
          finish('not_got');
          return;
      }
    }
    finish('not_got');
  }

  /** Acts on what the door answered the press. */
  async function answered(answer: AskAnswer) {
    if (answer.outcome !== 'failed') minted.current = null;
    switch (answer.outcome) {
      case 'sent':
        await waitForLink(answer.id);
        return;
      case 'signed_out':
        finish(null);
        signed.onSignedOut();
        return;
      case 'refused':
        if (answer.code === NOT_READY) stillMaking();
        else finish('not_got');
        return;
      case 'not_found':
        finish('not_got');
        reload();
        return;
      case 'too_many':
        if (mounted.current) setLimit(answer.limit);
        finish('too_many');
        return;
      default:
        finish('not_got');
    }
  }

  async function press() {
    if (out.current) return;
    out.current = true;
    setGetting(true);
    setEnded(null);
    minted.current ??= crypto.randomUUID();
    await answered(await askForDocument(minted.current, household, invoiceId, documentId));
  }

  // A link named this PDF (P107): pressed once, as it is first drawn, the same press as hers. A press
  // already out refuses a second, so a second run of this changes nothing.
  useEffect(() => {
    if (!pressNow) return;
    onPressed?.();
    void press();
    // Only as the button is first drawn: a later render is never a new press.
  }, []);

  if (beingMade) return <BeingMade name={name} lineRef={madeLine} />;

  return (
    <>
      <button
        type="button"
        aria-disabled={getting ? true : undefined}
        aria-busy={getting ? true : undefined}
        onClick={() => void press()}
        className={`${CARD_BUTTON} text-center text-pretty aria-disabled:cursor-wait`}
      >
        {getting ? (
          // Ops's words, and under them the PDF it gets, one above the other in the button's
          // middle; the space between them is read, never drawn.
          <span className="flex flex-col items-center py-1">
            <span>{DOCUMENTS.getting}</span> <span className="text-small font-normal">{name}</span>
          </span>
        ) : (
          label
        )}
      </button>
      <div role="status">
        {ended === null ? null : (
          <p className="mt-2 text-body text-pretty">
            {ended === 'too_many' ? tooManyBeforePhone(limit) : DOCUMENTS.notGotBeforePhone}{' '}
            <Phone after="." />
          </p>
        )}
      </div>
    </>
  );
}
